import _ from 'lodash';
import eachOfLimit from 'async/eachOfLimit';

export const FAVORITES_URL = '/favorites';

import state from './listingState';
import { getters, mutations, actions, FETCH_URL, RESET_FILTERS_URL } from './listing.js';

import TagHumansConfig from 'Config/authenticated/TagHumans';
import { lightenDarkenColor, hexToRgb } from 'utils/color';

export default {
    namespaced: true,
    state() {
        return JSON.parse(JSON.stringify(state));
    },
    getters: getters,
    mutations: {
        ...mutations,

        resetRows(state) {
            EventBus.emit('listing:map:reset');
        },
        appendMarkers(state, { data, reset, pagination }) {
            if (data.length == 0) {
                EventBus.emit('listing:map:fetch', { rowset: [], pagination: null });
                return;
            }

            if (reset) {
                EventBus.emit('listing:map:reset');
            }

            const rowset = [];

            // Construct marker in svg
            const drawMarkerSvg = (color, darkerColor) => `
                <svg width="50px" height="82px" viewBox="0 0 50 82" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
                    <title>locator_base</title>
                    <g id="locator_base" style="stroke: rgb(${darkerColor});" stroke-width="1" stroke-linejoin="round" stroke-linecap="round" fill="${color}" fill-rule="evenodd">
                        <path d="M25,2.13162821e-14 C38.8071187,2.13162821e-14 50,10.0632416 50,25.5316208 C50,35.8438736 41.6666667,54.6666667 25,82 C8.33333333,54.6666667 0,35.8438736 0,25.5316208 C0,10.0632416 11.1928813,2.13162821e-14 25,2.13162821e-14 Z M25,15 C19.4771525,15 15,19.4771525 15,25 C15,30.5228475 19.4771525,35 25,35 C30.5228475,35 35,30.5228475 35,25 C35,19.4771525 30.5228475,15 25,15 Z" id="locator"></path>
                    </g>
                </svg>`;

            // Encode svg marker to work with osm
            const iconSvgWithColor = (color, darkerColor) =>
                encodeURI(`data:image/svg+xml;utf8,${drawMarkerSvg(color, darkerColor)}`).replace(
                    '#',
                    '%23',
                );

            for (const row of data) {
                if (row.geo && row.geo.lat != undefined) {
                    // Get good color for marker according with humanTag based on type relation
                    const { bgcolor } = TagHumansConfig[row.relationType];
                    const updatedType =
                        row.relationType === 'person' ? 'individual' : row.relationType;

                    const root = document.documentElement;
                    // Retrieve the current value of a CSS variable
                    const hexColor = getComputedStyle(root).getPropertyValue(bgcolor);

                    // Add custom marker
                    const customIcon = L.icon({
                        className: row.relationType,
                        iconUrl: iconSvgWithColor(
                            hexColor,
                            hexToRgb(lightenDarkenColor(hexColor, -50)),
                        ),
                        iconSize: [25, 41],
                        iconAnchor: [12.5, 41],
                        popupAnchor: [12, 0],
                        shadowUrl: require('leaflet/dist/images/marker-shadow.png').default,
                    });

                    const marker = L.marker([row.geo.lat, row.geo.lng], {
                        icon: customIcon,
                        relationType: row.relationType,
                        bgColor: hexColor,
                    });
                    marker.bindPopup(function (layer) {
                        const tagHuman = TagHumansConfig[row.relationType];
                        let parts = [row.geo.part1, row.geo.part2, row.geo.part3, row.geo.part4];

                        let town = [row.geo.zip, row.geo.town].join(' ');
                        if (row.geo.state) {
                            town += ', ' + row.geo.state;
                        }

                        parts.push(town);
                        parts = parts.filter((item) => !!item).join('<br />');

                        return `
                            <h3 class="mt-0 fs-3">
                                <a class="link" target="_top" href="${row.url}">${row.name}</a>
                            </h3>
                            <p class="content flex-column flex-top m-0">
                                ${parts}
                                <span class="tag-third-map tag-third-map--${updatedType} typographies-paragraph-small-semibold">
                                    ${tagHuman.label}
                                </span>
                            </p>  
                        `;
                    });

                    rowset.push(marker);
                }
            }
            EventBus.emit('listing:map:fetch', { rowset, pagination });
        },

        resetPagination(state) {
            state.pagination = {
                current_page: 1,
                per_page: 500,
                last_page: '',
                total: '',
            };
        },
    },
    actions: {
        ...actions,

        init({ commit }, props) {
            commit('init', props);

            for (const i in props.filter) {
                const filter = {
                    name: i,
                    data: props.filter[i],
                    pending: true,
                };

                commit('applyFilter', filter);
            }

            commit('resetPagination');
            commit('resetRows');
        },

        refreshListing({ dispatch, state, commit }, data) {
            commit('setupPagination', data.meta.pagination);
            commit('updateFilters', data.meta.filters);
            commit('updateColumns', Object.values(data.meta.columns));
            commit('updateSorting', data.meta.sorting);
            commit('updatePermissions', data.permissions);
            commit('updateFullExport', data.hasFullExport);

            if (!state.init && state.pagination.current_page == 1) {
                commit('resetRows');
            }

            if (state.init) {
                commit('updateBatchActions', Object.values(data.batchActions));
                commit('updateDisplay', data.meta.display);
                commit('flagInit');
            }

            return data;
        },

        refreshData({ dispatch, state, commit }, data) {
            if (data.kpis) {
                commit('updateKpis', data.kpis);
            }

            const payload = {
                data: Object.keys(data.data).length > 0 ? data.data : [],
                reset: state.pagination.current_page == 1,
                pagination: state.pagination,
            };

            commit('appendMarkers', payload);
        },

        resetFilters({ dispatch, commit, getters, state }, apply = true) {
            for (const filter of getters.activeFilters) {
                commit('removeFilter', filter);
            }
            for (const filter of getters.pendingFilters) {
                commit('removeFilter', filter);
            }

            commit('resetPagination');

            if (apply) {
                commit(
                    'setAjax',
                    SellsyJS.WebServiceCaller.del(
                        `/listing/${state.listingname}/filters`,
                        {},
                        function (json) {
                            commit('setAjax', false);
                            if (json.error === undefined) {
                                dispatch('refreshListing', json).then(() => {
                                    dispatch('refreshData', json);
                                    dispatch('fetchNextPage');
                                    if (getters.activeFilters.length === 0) {
                                        commit('updateFiltersView', 'select');
                                    } else {
                                        commit('updateFiltersView', 'summary');
                                    }
                                });
                                resolve(json);
                            } else {
                                commit('setError');
                                reject;
                            }
                        },
                        function () {
                            commit('setError');
                            reject;
                        },
                    ),
                );
            }
        },

        fetch({ dispatch, getters, state, commit }, overwriteMeta) {
            if (state.error) {
                commit('setError', false);
            }

            var data = {
                meta: getters.meta,
                ...state.params,
            };

            data.meta.columns = ['addresscoords'];
            data.meta.sorting = {
                field: '',
                direction: '',
            };

            if (state.init) {
                data.meta.pagination = state.pagination;
                data.init = state.init;
            }

            if (state.init && state.favorite) {
                data.favorite = state.favorite;
            }

            if (overwriteMeta !== undefined && overwriteMeta.page !== undefined) {
                data.meta.pagination.current_page = overwriteMeta.page;
            } else if (overwriteMeta !== undefined) {
                data.meta = overwriteMeta;
                data.meta.pagination = { current_page: 1 };
            }

            // Loop on pagination to collect all map markers
            return new Promise((resolve, reject) => {
                if (data.meta.pagination.current_page == 1) commit('setAjax', true);
                SellsyJS.WebServiceCaller.post(
                    `${FETCH_URL}/${state.listingname}`,
                    {},
                    data,
                    (response) => {
                        if (data.meta.pagination.current_page == 1) {
                            commit('setAjax', false);
                            EventBus.emit('listing:map:resetZoom');
                            dispatch('refreshListing', response).then(() => {
                                dispatch('refreshData', response);
                                dispatch('fetchNextPage');
                            });
                        } else {
                            if (
                                !!window.sellsyapp.config.globalProperties.$storeHasModule(
                                    state.listingHash,
                                )
                            )
                                dispatch('refreshData', response);
                        }
                        resolve(response);
                    },
                    (response) => {
                        reject();
                        dispatch('endFetch');
                        commit('setError');
                    },
                );
            });
        },

        endFetch({ commit }) {
            commit('updateCurrentPage', 1);
        },

        fetchNextPage({ dispatch, state, commit }, overwriteMeta) {
            if (state.pagination.last_page == 0) {
                //empty result
                dispatch('endFetch');
                return;
            }

            eachOfLimit(
                new Array(state.pagination.last_page - 1),
                1,
                (item, key, callback) => {
                    const page = key + 2; //index 0 + firstpage
                    if (
                        !!window.sellsyapp.config.globalProperties.$storeHasModule(
                            state.listingHash,
                        )
                    )
                        dispatch('fetch', { page: page }).then(() => callback());
                },
                () => {
                    dispatch('endFetch');
                },
            );
        },
    },
};
